import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import ImageProfil from "../Profil/ImageProfil";

const MAX_RESULTS = 8;

const SearchPalette = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const usersData = useSelector((state) => state.usersReducer);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
      setSelectedIndex(0);
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [open, onClose]);

  const results = useMemo(() => {
    const search = debouncedQuery.trim().toLowerCase();
    if (!Array.isArray(usersData) || !search) return [];
    return usersData
      .filter(
        (user) =>
          user.firstname?.toLowerCase().includes(search) ||
          user.name?.toLowerCase().includes(search) ||
          `${user.firstname} ${user.name}`.toLowerCase().includes(search)
      )
      .slice(0, MAX_RESULTS);
  }, [usersData, debouncedQuery]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery]);

  const openProfile = (id) => {
    onClose();
    navigate(`/userprofil/${id}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      openProfile(results[selectedIndex].id);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="palette-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="palette" role="dialog" aria-label="Rechercher un utilisateur">
        <div className="palette-input">
          <Search size={17} className="palette-input-icon" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Rechercher un utilisateur…"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button className="palette-close" onClick={onClose} aria-label="Fermer">
            <X size={15} />
          </button>
        </div>

        <div className="palette-results">
          {!debouncedQuery.trim() ? (
            <p className="palette-hint">Tape un nom ou un prénom…</p>
          ) : results.length === 0 ? (
            <p className="palette-hint">Aucun utilisateur trouvé</p>
          ) : (
            <>
              <p className="palette-label">
                Utilisateurs — {results.length} résultat{results.length > 1 ? "s" : ""}
              </p>
              <ul>
                {results.map((user, index) => (
                  <li
                    key={user.id}
                    className={index === selectedIndex ? "selected" : ""}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => openProfile(user.id)}
                  >
                    <span className="avatar">
                      <span className="avatar-initials">
                        {(user.firstname?.[0] || "") + (user.name?.[0] || "")}
                      </span>
                      <ImageProfil id={user.id} />
                    </span>
                    <span className="palette-user">
                      <span className="palette-user-name">
                        {user.firstname} {user.name}
                      </span>
                      {user.service && (
                        <span className="palette-user-service">{user.service}</span>
                      )}
                    </span>
                    {index === selectedIndex && <kbd>↵</kbd>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="palette-footer">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> naviguer
          </span>
          <span>
            <kbd>↵</kbd> ouvrir le profil
          </span>
          <span>
            <kbd>Échap</kbd> fermer
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchPalette;
